/** SCR-07 / 08 / 11 — Deals / Leads: the lead list, the Add Lead form, and the read-only deals list. */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { PageHeader, Card, Badge, Modal, Field, Tabs, SearchInput, EmptyState, Spinner, ErrorState } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { DataTable } from '../../components/common/DataTable.jsx';
import { useDebounce } from '../../hooks/useDebounce.js';
import { pushToast } from '../ui/uiSlice.js';
import { money, date } from '../../utils/formatters.js';
import * as api from '../../api/partnerPortal.api.js';
import { SubmissionBadge, SUBMISSION, INDUSTRIES, TIMELINES, CURRENCIES, errorText, fieldErrors } from './partnerShared.jsx';

const COUNTRIES = ['India', 'United States', 'United Kingdom', 'United Arab Emirates', 'Singapore', 'Australia', 'Canada', 'Germany', 'France', 'Netherlands', 'Saudi Arabia', 'Qatar', 'Bangladesh', 'Sri Lanka', 'Nepal', 'Malaysia', 'Indonesia', 'Japan', 'South Africa', 'Kenya', 'Nigeria', 'Other'];

const useCursorList = (key, fn, params) => useInfiniteQuery({
  queryKey: key, queryFn: ({ pageParam }) => fn({ ...params, cursor: pageParam, limit: 25 }),
  initialPageParam: undefined, getNextPageParam: (l) => l.page?.next_cursor ?? undefined,
});

export default function PartnerLeadsPage() {
  const me = useOutletContext();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') ?? 'leads';
  const status = sp.get('status') ?? '';
  const member = sp.get('member') ?? '';
  const [q, setQ] = useState('');
  const dq = useDebounce(q, 300);
  const [adding, setAdding] = useState(sp.get('new') === '1');
  const set = (patch) => { const n = new URLSearchParams(sp); Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k))); setSp(n, { replace: true }); };
  useEffect(() => { if (sp.get('new') === '1') { setAdding(true); set({ new: '' }); } }, [sp]); // eslint-disable-line react-hooks/exhaustive-deps

  const leadsQ = useCursorList(['partner', 'leads', { status, dq, member }], api.listLeads, { status, q: dq, submitted_by: member });
  const dealsQ = useCursorList(['partner', 'deals'], api.listDeals, {});
  const teamQ = useQuery({ queryKey: ['partner', 'team'], queryFn: api.listTeam, enabled: me.role === 'admin' });
  const rows = (leadsQ.data?.pages ?? []).flatMap((p) => p.data);
  const dealRows = (dealsQ.data?.pages ?? []).flatMap((p) => p.data);
  const isAdmin = me.role === 'admin';

  const columns = [
    { key: 'partner_lead_id', header: 'Lead ID', render: (r) => <span className="fw-600">{r.partner_lead_id}</span> },
    { key: 'lead_name', header: 'Lead', render: (r) => (<div><div className="fw-600">{r.lead_name}</div><div className="t-xs text-muted">{r.contact_person}</div></div>) },
    { key: 'product_interest', header: 'Interest', render: (r) => (<div>{r.product_interest}<div className="t-xs text-muted">{r.industry}</div></div>) },
    { key: 'estimated_value', header: 'Value', align: 'end', render: (r) => (r.estimated_value != null ? money(r.estimated_value, r.currency) : '—') },
    { key: 'submission_status', header: 'Status', render: (r) => (<div><SubmissionBadge status={r.submission_status} />{r.crm_lead?.status && <div className="t-2xs text-muted mt-1">CRM: {r.crm_lead.status}</div>}</div>) },
    ...(isAdmin ? [
      { key: 'submitted_by', header: 'Submitted by', render: (r) => r.submitted_by.name },
      { key: 'assigned_member', header: 'Assigned to', render: (r) => r.assigned_member?.name ?? '—' },
    ] : []),
    { key: 'sales_rep', header: 'Sales rep', render: (r) => r.sales_rep?.name ?? '—' },
    { key: 'submitted_at', header: 'Submitted', render: (r) => (r.submitted_at ? date(r.submitted_at) : 'Not submitted') },
  ];
  const dealCols = [
    { key: 'deal_id', header: 'Deal ID', render: (r) => <span className="fw-600">{r.deal_id}</span> },
    { key: 'deal_name', header: 'Deal', render: (r) => (<div><div className="fw-600">{r.deal_name}</div><div className="t-xs text-muted">{r.customer_name ?? '—'}</div></div>) },
    { key: 'related_lead', header: 'Related lead' },
    { key: 'estimated_value', header: 'Value', align: 'end', render: (r) => money(r.estimated_value, r.currency) },
    { key: 'deal_stage', header: 'Stage', render: (r) => <Badge tone={r.is_won ? 'emerald' : r.is_closed ? 'rose' : 'sky'}>{r.deal_stage}</Badge> },
    { key: 'sales_representative', header: 'Sales rep', render: (r) => r.sales_representative ?? '—' },
    { key: 'expected_closing_date', header: 'Expected close', render: (r) => date(r.expected_closing_date) },
    { key: 'last_updated', header: 'Updated', render: (r) => date(r.last_updated) },
  ];

  return (
    <>
      <PageHeader title="Deals / Leads" subtitle={me.role === 'admin' ? 'All leads submitted by your company' : 'Leads you have submitted'}>
        <button type="button" className="btn btn-add btn-sm-x" onClick={() => setAdding(true)}><i className="bi bi-plus-lg me-1" aria-hidden="true" />Add new lead</button>
      </PageHeader>

      <Tabs value={tab} onChange={(t) => set({ tab: t === 'leads' ? '' : t })} tabs={[{ key: 'leads', label: 'Leads' }, { key: 'deals', label: 'Deals' }]} />

      {tab === 'leads' && (
        <Card className="mt-3" bare>
          <div className="d-flex flex-wrap gap-2 p-3 pb-2 align-items-center">
            <SearchInput value={q} onChange={setQ} placeholder="Search lead, contact, ID…" />
            <select className="form-select form-select-sm w-auto" value={status} onChange={(e) => set({ status: e.target.value })}>
              <option value="">All statuses</option>
              {Object.entries(SUBMISSION).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            {isAdmin && (
              <select className="form-select form-select-sm w-auto" value={member} onChange={(e) => set({ member: e.target.value })}>
                <option value="">All members</option>
                {(teamQ.data ?? []).map((m) => <option key={m.id} value={m.user_id}>{m.full_name}</option>)}
              </select>
            )}
          </div>
          <DataTable columns={columns} rows={rows} isLoading={leadsQ.isLoading} error={leadsQ.error} onRetry={leadsQ.refetch}
            onRowClick={(r) => navigate(`/partner/leads/${r.id}`)} emptyTitle="No leads yet" />
          {leadsQ.hasNextPage && <div className="p-3 text-center"><button type="button" className="btn btn-edit btn-sm-x" onClick={() => leadsQ.fetchNextPage()} disabled={leadsQ.isFetchingNextPage}>{leadsQ.isFetchingNextPage ? <ButtonSpinner /> : 'Load more'}</button></div>}
        </Card>
      )}

      {tab === 'deals' && (
        <Card className="mt-3" subtitle="Deals created from your approved leads. Read-only." bare>
          <DataTable columns={dealCols} rows={dealRows} isLoading={dealsQ.isLoading} error={dealsQ.error} onRetry={dealsQ.refetch} emptyTitle="No deals yet" />
        </Card>
      )}

      <LeadFormModal open={adding} onClose={() => setAdding(false)} onDone={(l) => { setAdding(false); navigate(`/partner/leads/${l.id}`); }} />
    </>
  );
}

const BLANK = { lead_name: '', contact_person: '', designation: '', email: '', mobile: '', website: '', industry: '', country: '', state_city: '', product_interest: '', estimated_value: '', currency: 'INR', purchase_timeline: '', description: '' };

export function LeadFormModal({ open, onClose, onDone, lead }) {
  const editing = Boolean(lead);
  const dispatch = useDispatch();
  const qc = useQueryClient();
  const [f, setF] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState([]);
  const [dup, setDup] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    if (open) {
      setF(lead ? Object.fromEntries(Object.keys(BLANK).map((k) => [k, lead[k] == null ? BLANK[k] : String(lead[k])])) : BLANK);
      setErrors({}); setFiles([]); setDup(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const checkDup = async () => {
    if (!f.email && !f.mobile && !f.lead_name) return;
    try { setDup(await api.duplicateCheck({ email: f.email, mobile: f.mobile, lead_name: f.lead_name })); } catch { /* advisory only */ }
  };

  const validate = (draft) => {
    const e = {};
    const req = draft ? ['lead_name'] : ['lead_name', 'contact_person', 'email', 'mobile', 'industry', 'country', 'product_interest', 'description'];
    req.forEach((k) => { if (!String(f[k]).trim()) e[k] = 'Required'; });
    if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = 'Enter a valid email';
    if (f.estimated_value !== '' && (Number(f.estimated_value) < 0 || Number.isNaN(Number(f.estimated_value)))) e.estimated_value = 'Enter a valid amount';
    if (f.estimated_value !== '' && !f.currency) e.currency = 'Choose a currency';
    return e;
  };

  const submit = async (draft) => {
    const e = validate(draft);
    setErrors(e);
    if (Object.keys(e).length) return;
    const body = Object.fromEntries(Object.entries(f).filter(([, v]) => String(v).trim() !== '').map(([k, v]) => [k, k === 'estimated_value' ? Number(v) : String(v).trim()]));
    if (body.estimated_value == null) delete body.currency;
    if (draft && !editing) body.save_as_draft = true;
    setBusy(true);
    try {
      const res = editing ? await api.updateLead(lead.id, body, lead.row_version) : await api.submitLead(body);
      for (const file of files) {
        try { await api.uploadDocument(res.id, file); } catch (ex) { dispatch(pushToast({ tone: 'danger', message: `Could not upload ${file.name}`, detail: errorText(ex) })); }
      }
      qc.invalidateQueries({ queryKey: ['partner'] });
      dispatch(pushToast({ tone: 'success', message: editing ? 'Lead updated' : draft ? 'Draft saved' : `Lead ${res.partner_lead_id} submitted for review` }));
      onDone(res);
    } catch (ex) {
      setErrors(fieldErrors(ex));
      dispatch(pushToast({ tone: 'danger', message: 'Could not save the lead', detail: errorText(ex) }));
    } finally { setBusy(false); }
  };

  const inp = (k, label, extra = {}, props = {}) => (
    <div className={extra.col ?? 'col-md-6'}>
      <Field label={label} required={extra.required} error={errors[k]}>
        <input className="form-control" value={f[k]} onChange={set(k)} onBlur={['email', 'mobile', 'lead_name'].includes(k) ? checkDup : undefined} {...props} />
      </Field>
    </div>
  );
  const sel = (k, label, options, extra = {}) => (
    <div className={extra.col ?? 'col-md-6'}>
      <Field label={label} required={extra.required} error={errors[k]}>
        <select className="form-select" value={f[k]} onChange={set(k)}><option value="">Select…</option>{options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
      </Field>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={editing ? `Edit ${lead.partner_lead_id}` : 'Add new lead'} size="lg"
      footer={(<>
        <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
        {!editing && <button type="button" className="btn btn-edit btn-sm-x" disabled={busy} onClick={() => submit(true)}>Save as draft</button>}
        <button type="button" className="btn btn-save btn-sm-x" disabled={busy} onClick={() => submit(editing && lead.submission_status === 'draft')}>
          {busy ? <><ButtonSpinner />Saving…</> : editing ? (lead.submission_status === 'information_requested' ? 'Save & resubmit' : 'Save changes') : 'Submit for review'}
        </button>
      </>)}>
      {dup?.has_potential_duplicate && (
        <div className="alert alert-warning py-2 t-sm">
          <i className="bi bi-exclamation-triangle me-1" aria-hidden="true" />
          This lead may already exist ({dup.warnings.map((w) => (w.field === 'company' ? 'similar company name' : `same ${w.field}`)).join(', ')}). You can still submit — the Sales Manager makes the final decision.
        </div>
      )}
      <div className="eyebrow mb-2">Customer</div>
      <div className="row g-3 mb-3">
        {inp('lead_name', 'Lead / Company name', { required: true, col: 'col-md-8' })}
        {inp('website', 'Website', { col: 'col-md-4' }, { placeholder: 'https://' })}
        {inp('contact_person', 'Contact person', { required: true })}
        {inp('designation', 'Designation')}
        {inp('email', 'Email', { required: true }, { type: 'email' })}
        {inp('mobile', 'Mobile number', { required: true }, { type: 'tel' })}
        {sel('industry', 'Industry', INDUSTRIES, { required: true, col: 'col-md-4' })}
        {sel('country', 'Country', COUNTRIES, { required: true, col: 'col-md-4' })}
        {inp('state_city', 'State / City', { col: 'col-md-4' })}
      </div>
      <div className="eyebrow mb-2">Opportunity</div>
      <div className="row g-3 mb-3">
        {inp('product_interest', 'Product / Service interest', { required: true, col: 'col-md-12' }, { placeholder: 'e.g. CRM implementation, AI automation' })}
        {inp('estimated_value', 'Estimated deal value', { col: 'col-md-4' }, { type: 'number', min: 0, step: 'any' })}
        {sel('currency', 'Currency', CURRENCIES, { col: 'col-md-4' })}
        {sel('purchase_timeline', 'Expected purchase timeline', TIMELINES, { col: 'col-md-4' })}
        <div className="col-12"><Field label="Lead description" required error={errors.description}><textarea className="form-control" rows={4} value={f.description} onChange={set('description')} placeholder="What does the customer need, and what is the context?" /></Field></div>
      </div>
      <div className="eyebrow mb-2">Attachments</div>
      <input ref={fileRef} type="file" multiple hidden onChange={(e) => { setFiles((x) => [...x, ...e.target.files]); e.target.value = ''; }} />
      <button type="button" className="btn btn-edit btn-sm-x" onClick={() => fileRef.current?.click()}><i className="bi bi-paperclip me-1" aria-hidden="true" />Add files</button>
      <div className="mt-2 d-flex flex-wrap gap-1">{files.map((file, i) => (
        <span key={i} className="pill pill-violet d-inline-flex align-items-center gap-1">{file.name}<button type="button" className="btn-close btn-sm" style={{ fontSize: 8 }} aria-label="Remove" onClick={() => setFiles((x) => x.filter((_, j) => j !== i))} /></span>
      ))}</div>
    </Modal>
  );
}
